import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/designSystem/Dialog';
import { Button } from '@/designSystem/Button';
import { Input } from '@/designSystem/Input';
import { AlertCircle, CheckCircle, Loader2, RefreshCw } from 'lucide-react';

interface SubscriptionData {
  email: string;
  firstName: string;
  lastName: string;
}

interface ConfirmEmailModalProps {
  isOpen: boolean;
  onSubmit: (data: SubscriptionData) => Promise<void>;
  isSubmitting?: boolean;
  submissionError?: Error | null;
}

const ConfirmEmailModal: React.FC<ConfirmEmailModalProps> = ({
  isOpen,
  onSubmit,
  isSubmitting = false,
  submissionError = null,
}) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): string | null => {
    if (!firstName.trim()) {
      return 'First name is required';
    }
    if (!lastName.trim()) {
      return 'Last name is required';
    }
    if (!email.trim()) {
      return 'Email address is required';
    }
    if (!validateEmail(email)) {
      return 'Please enter a valid email address';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    const error = validateForm();
    if (error) {
      setValidationError(error);
      return;
    }

    try {
      await onSubmit({
        email: email.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      setSubmitted(true);
    } catch (err) {
      // Error handling is managed by the parent through submissionError
      console.error('Subscription error:', err);
    }
  };

  const resetForm = () => {
    setEmail('');
    setFirstName('');
    setLastName('');
    setSubmitted(false);
    setValidationError(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => false}>
      <DialogContent hideCloseButton className="sm:max-w-md" onEscapeKeyDown={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Unlock Property Health Report</DialogTitle>
          <DialogDescription>
            We require you to subscribe to our email list to access the property health report. Please enter your details below to continue.
          </DialogDescription>
        </DialogHeader>

        {!submitted ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <div className="text-sm font-medium mb-1">First Name</div>
                <Input
                  id="firstName"
                  type="text"
                  placeholder="John"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  className="w-full"
                />
              </div>
              <div className="space-y-2">
                <div className="text-sm font-medium mb-1">Last Name</div>
                <Input
                  id="lastName"
                  type="text"
                  placeholder="Doe"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  className="w-full"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium mb-1">Email Address</div>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full"
              />
            </div>

            {(validationError || submissionError) && (
              <div className="flex items-center text-red-500 text-sm mt-1">
                <AlertCircle className="h-4 w-4 mr-1" />
                {validationError || (submissionError instanceof Error ? submissionError.message : 'An error occurred')}
              </div>
            )}

            <div className="text-sm text-gray-500">
              We never spam.
            </div>

            <Button type="submit" disabled={isSubmitting} className="w-full">
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Subscribing...
                </>
              ) : (
                'Subscribe to Continue'
              )}
            </Button>
          </form>
        ) : (
          <div className="py-4">
            <div className="flex items-center justify-center mb-4 text-green-500">
              <CheckCircle className="h-12 w-12" />
            </div>
            <h2 className="text-xl font-bold text-center mb-3">Check Your Email</h2>
            <p className="text-center mb-4">
              Please check your inbox for a confirmation email. You must confirm your subscription to access the property health report.
            </p>
            <div className="bg-blue-50 p-4 rounded-md mb-6">
              <p className="text-sm text-blue-700 text-center">
                <strong>Important:</strong> You cannot access the property health report until you've confirmed your email.
              </p>
            </div>
            <div className="border-t border-gray-200 pt-4">
              <p className="text-gray-500 text-sm text-center mb-4">
                Didn't receive the email or need to change your details?
              </p>
              <Button
                type="button"
                variant="outline"
                className="w-full flex items-center justify-center"
                onClick={resetForm}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again with Different Details
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmEmailModal;